import { NgIf, NgFor, TitleCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { NbDateService, NbDialogRef, NbDialogService, NbToastrService, NbCardModule, NbIconModule, NbButtonModule, NbInputModule, NbCheckboxModule, NbSelectModule, NbOptionModule } from '@nebular/theme';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux';
import { FREQUENCIES } from 'src/app/models/frequencies';
import { Insight } from 'src/app/models/insight';
import { ACTIONS, CONFIG_REGEX } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { SharedModule } from '../../../../shared/shared.module';
import { PostInsights, SaveInsightComplete } from '../../redux';

@Component({
    selector: 'app-edit-insight',
    templateUrl: './edit-insight.component.html',
    styleUrls: ['./edit-insight.component.scss'],
    imports: [NbCardModule, NgIf, NbIconModule, NbButtonModule, FormsModule, NgSelectComponent, NgFor, NgOptionComponent, NbInputModule, SharedModule, CodemirrorModule, NbCheckboxModule, NbSelectModule, NbOptionModule, OwlDateTimeModule, TitleCasePipe]
})
export class EditInsightComponent implements OnInit, OnDestroy {
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public FREQUENCIES = FREQUENCIES;
  public theme = '';

  public insight: Insight;
  public action: string;
  public thisRegex = CONFIG_REGEX;
  public showDatepicker = false;
  public dateForDatepickerStart: Date;
  public dateForDatepickerEnd: Date;

  //Using to disable future dates in datepicker
  public minStartDate: Date;
  public minEndDate: Date;

  public username = '';

  public categories = [
    'KOZ_recommendation',
    'SCM',
    'coach',
    'coaching',
    'ecommerce',
    'education',
    'first-time',
    'iRobotFor message',
    'low_usage_assist',
    'maintenance',
    'maintenance_reminder',
    'marketing',
    'nps',
    'onboarding',
    'schedule_recommendation',
    'seasonal_recommendation',
    'smart-home',
    'smart-maps',
    'start_refuse_assist',
    'support',
    'testing_microfleet_racer',
    'warnings',
    'welcome_optimize_reminder'
  ];

  constructor(
    private store: Store,
    protected ref: NbDialogRef<EditInsightComponent>,
    private action$: Actions,
    private dialogService: NbDialogService,
    protected dateService: NbDateService<Date>,
    private toastrService: NbToastrService
  ) {
    this.minStartDate = this.dateService.addMonth(this.dateService.today(), 0);
    this.minEndDate = this.dateService.addDay(this.dateService.today(), 1);
  }

  public ngOnInit() {
    const targetInsight = new Insight();
    Object.assign(targetInsight, this.insight);
    this.insight = targetInsight;

    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.action$.pipe(ofActionCompleted(SaveInsightComplete), takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.ref.close();
      }
    });
    this.showDatepicker = this.insight.enabled;

    this.convertDateForPicker();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public checkedActivated() {
    this.showDatepicker = !this.insight.enabled;
  }

  public save() {
    if (this.insight.enabled) {
      if (this.dateForDatepickerStart && this.dateForDatepickerEnd) {
        this.insight.start_date = Math.round(this.dateForDatepickerStart.getTime() / 1000);
        this.insight.end_date = Math.round(this.dateForDatepickerEnd.getTime() / 1000);
      } else {
        this.toastrService.show(status, $localize`Start and End Time is required for an Activated Insight.`, {
          status: 'danger'
        });

        // We don't want to execute the save API call
        return;
      }
    } else {
      this.insight.start_date = null;
      this.insight.end_date = null;
    }
    if (this.action === ACTIONS.CLONE) {
      this.insight.created_by = this.username;
      this.insight.last_updated_by = this.username;
    }
    if ('id' in this.insight) {
      this.insight.last_updated_by = this.username;
    } else {
      this.insight.created_by = this.username;
      this.insight.last_updated_by = this.username;
    }

    this.store.dispatch(new PostInsights(this.insight));
  }

  public close() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Confirm Close`,
          message: $localize`Are you sure that you want to close the window? Any changes that you have made will be lost.`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
  }
  public changeEvent() {
    this.compareStartEndDate();
  }

  public convertDateForPicker() {
    if (this.insight.start_date) {
      this.dateForDatepickerStart = new Date(this.insight.start_date * 1000);
    }
    if (this.insight.end_date) {
      this.dateForDatepickerEnd = new Date(this.insight.end_date * 1000);
    }

    if (this.insight.start_date < this.insight.end_date) {
      this.minEndDate = this.dateService.addDay(new Date(this.insight.start_date * 1000), 1);
    }
  }

  public compareStartEndDate() {
    if (this.dateForDatepickerEnd) {
      if (this.dateForDatepickerStart.getTime() > this.dateForDatepickerEnd.getTime()) {
        this.dateForDatepickerEnd = this.dateService.addDay(new Date(this.dateForDatepickerStart.getTime()), 1);
        this.minEndDate = this.dateService.addDay(new Date(this.dateForDatepickerStart.getTime()), 1);
      } else {
        this.minEndDate = this.dateService.addDay(new Date(this.dateForDatepickerStart.getTime()), 1);
      }
    } else {
      this.minEndDate = this.dateService.addDay(new Date(this.dateForDatepickerStart.getTime()), 1);
    }
  }
}
